import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { doc, getDoc } from "firebase/firestore";
import { toast, Bounce } from "react-toastify";
import { Spin } from "antd";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";
import {
  calculateAllowableRefund,
  getChargeableRideFare,
} from "../../utils/calculateAllowableRefund";

Modal.setAppElement("#root");

const PaymentModal2 = ({
  isOpen,
  onRequestClose,
  onConfirm,
  ride,
  isRefundModal = false,
  processing = false,
}) => {
  const [amount, setAmount] = useState("");
  const [allowableRefund, setAllowableRefund] = useState(null);
  const [refundBaseFare, setRefundBaseFare] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAmount("");
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchRefundData = async () => {
      if (!isOpen || !isRefundModal) {
        setAllowableRefund(null);
        setRefundBaseFare(null);
        setLoadingRefund(false);
        return;
      }

      const rideId = String(ride?.ride_id || ride?.rideId || "").trim();
      if (!rideId) {
        setAllowableRefund(null);
        setRefundBaseFare(null);
        setLoadingRefund(false);
        return;
      }

      setLoadingRefund(true);
      try {
        const rideSnap = await getDoc(
          doc(db, COLLECTION_NAME.ridesHistory, rideId)
        );
        const historyData = rideSnap.exists() ? rideSnap.data() : {};
        // Refund-request row can have stale total_ride_fare; rides history has retained `price`.
        const mergedRide = { ...ride, ...historyData };
        const baseFare = getChargeableRideFare(mergedRide);
        const isCancelled =
          "cancellationStatus" in mergedRide ||
          !!mergedRide.cancellationStatus;

        setRefundBaseFare(baseFare);
        const tipAmount = Math.max(
          0,
          Number.parseFloat(String(mergedRide.tip ?? 0)) || 0
        );
        const result = await calculateAllowableRefund(rideId, baseFare, {
          useRideHintIfNoNet: isCancelled,
          tipAmount,
        });
        setAllowableRefund(result);
      } catch (error) {
        console.error("Failed to fetch refund data:", error);
        setAllowableRefund(null);
        setRefundBaseFare(null);
      } finally {
        setLoadingRefund(false);
      }
    };

    fetchRefundData();
  }, [isOpen, isRefundModal, ride]);

  const handleConfirm = () => {
    const enteredAmount = parseFloat(amount.replace(/^0+/, ""));

    if (!amount || isNaN(enteredAmount) || enteredAmount <= 0) {
      toast.error("Please enter a valid amount.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return;
    }

    if (isRefundModal) {
      if (loadingRefund) return;
      const cap = parseFloat(allowableRefund || 0);
      if (enteredAmount > cap) {
        toast.error(
          cap <= 0
            ? "No refundable amount available for this ride."
            : "Entered amount exceeds the allowable refund amount.",
          {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          }
        );
        return;
      }
      onConfirm(enteredAmount, {
        totalRideFareCap: Math.max(
          refundBaseFare ?? 0,
          cap,
          parseFloat(ride?.total_ride_fare || 0)
        ),
      });
      return;
    }

    if (enteredAmount > (ride?.amount ?? 0)) {
      toast.error("Entered amount exceeds the outstanding balance.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return;
    }

    onConfirm(enteredAmount);
  };

  const handleCancel = () => {
    setAmount("");
    onRequestClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      contentLabel="Payment Confirmation"
      className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto mt-44"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50"
    >
      <h2 className="text-2xl text-dazzledBlue font-bold mb-2">
        {isRefundModal ? "Enter Refund Amount" : "Enter Amount"}
      </h2>
      {isRefundModal && (
        <>
          <h4 className="text-lg text-dazzledBlue font-semibold mb-2">
            Allowable refund amount:&nbsp;
            {loadingRefund ? <Spin size="madium" /> : `$${allowableRefund}`}
          </h4>
        </>
      )}

      <div className="mb-6">
        <label
          htmlFor="amount"
          className="block text-gray-500 text-lg font-medium mb-2"
        >
          Amount you want to pay:
        </label>
        <input
          type="number"
          id="amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Enter amount"
          className="w-full border border-gray-300 rounded-md p-2"
        />
      </div>
      <div className="flex justify-end space-x-4">
        <button
          disabled={processing}
          onClick={handleCancel}
          className={`bg-gray-200 text-gray-700 py-2 px-4 rounded-md transition-colors ${processing ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300"
            }`}
        >
          Cancel
        </button>
        <button
          disabled={loadingRefund || processing}
          onClick={handleConfirm}
          className="text-primary bg-celadon py-2 px-6 rounded-lg transition-colors"
        >
          {processing ? (
            <div className="w-6 h-6 border-4 border-t-white border-celadon-300 rounded-full animate-spin"></div>
          ) : (
            "Yes"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default PaymentModal2;
